import { useEffect } from 'react';
import { create } from 'zustand';
import { db } from '../../sync/db/database';
import { useSyncStore } from '../../sync/model/sync.store';
import { useAuthStore } from '../../auth/model/auth.store';
import {
  EMPTY_TASKS_DATA,
  isCompletionAlive,
  isLinkAlive,
  isProjectAlive,
  isTagAlive,
  isTaskAlive,
  type TasksData,
} from './task.types';

interface TasksState {
  data: TasksData;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

async function loadData(): Promise<TasksData> {
  const [tasks, tags, projects, checklistItems, taskTags, completions] = await Promise.all([
    db.tasks.toArray(),
    db.tags.toArray(),
    db.projects.toArray(),
    db.checklist_items.toArray(),
    db.task_tags.toArray(),
    db.completions.toArray(),
  ]);

  return {
    tasks: tasks.filter(isTaskAlive),
    tags: tags.filter(isTagAlive),
    projects: projects.filter(isProjectAlive),
    checklistItems: checklistItems.filter((item) => item.deleted_at === null),
    taskTags: taskTags.filter(isLinkAlive),
    completions: completions.filter(isCompletionAlive),
  };
}

export const useTasksStore = create<TasksState>((set) => ({
  data: EMPTY_TASKS_DATA,
  loading: true,
  error: null,

  refresh: async () => {
    try {
      const data = await loadData();
      set({ data, loading: false, error: null });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Не удалось загрузить задачи',
      });
    }
  },
}));

/** Хук данных задач: загрузка из Dexie + обновление при синхронизации. */
export function useTasksData(): TasksData & { loading: boolean; error: string | null } {
  const data = useTasksStore((state) => state.data);
  const loading = useTasksStore((state) => state.loading);
  const error = useTasksStore((state) => state.error);
  const refresh = useTasksStore((state) => state.refresh);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const lastSyncAt = useSyncStore((state) => state.lastSyncAt);
  const pendingCount = useSyncStore((state) => state.pendingCount);

  useEffect(() => {
    if (userId === null) {
      return;
    }
    void refresh();
  }, [userId, lastSyncAt, pendingCount, refresh]);

  return { ...data, loading, error };
}

export async function refreshTasks(): Promise<void> {
  await useTasksStore.getState().refresh();
}
